import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useLocation, useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { getThemeById } from '../../config/themes'
import { CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES, formatCustomerSlipCancelWindowText, getRoundImage, lotteryService } from '../../services/lotteryService'
import * as authService from '../../services/authService'
import { getRuntimeTenantIdSync } from '../../lib/runtimeTenant'
import { useLotteryCountdown } from '../../hooks/useLotteryCountdown'
import type { LotteryRound } from '../../services/lotteryService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import RulesGuideModal from '../../components/RulesGuideModal'
import { resolveSetPackageFixedPrice } from '../../utils/setLottoPackageUtils'
import { handleAuthExpired, handleUnauthorizedResponse } from '../../utils/sessionReplacedHandler'
import './ModernLobbyHuayLotteryTermsPage.css'

const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

/** แพ็คเกจการแทง — ใช้โครงเดียวกับ user-classic CommonTermsPage */
export interface BettingPackage {
  code: string
  name: string
  nameEn?: string
  description?: string
  category?: string
  packageType: 'RATE_BASED' | 'SET_BASED'
  isDefault: boolean
  displayOrder: number
  setConfig?: {
    fixedPrice: number
    fixedPayout: number
    discount: number
    includedPrizeTypes?: Array<{
      prizeTypeCode: string
      prizeTypeName: string
      digitCount: number
    }>
  }
  prizeConfigs?: Array<{
    prizeTypeCode: string
    prizeTypeName: string
    payoutRate: number
    discount: number
    minBet?: number
    maxBet?: number
  }>
  metadata?: {
    displayCard?: {
      title?: string
      payoutAmount?: number
      discountPercent?: number
    }
  }
}

function formatCloseTime(betEndTime: string): string {
  const d = new Date(betEndTime)
  return d.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getTenantId = (): string => {
  const tenantId = getRuntimeTenantIdSync()
  if (tenantId) return tenantId
  throw new Error('Tenant ID is required but not found.')
}

function isSetLottery(round: LotteryRound | null): boolean {
  if (!round) return false
  const combined = [
    round.huayTypeName,
    round.huayTypeCode,
    round.huayName,
    round.huayCode,
  ]
    .filter(Boolean)
    .join(' ')
  return /หวยชุด|เลขชุด|ชุด/i.test(combined)
}

function getSetPackageFamilyCode(code?: string): string {
  return String(code || '')
    .trim()
    .toUpperCase()
    .replace(/_(LARGE|MEDIUM|SMALL)$/i, '')
}

function getPreferredSetFamilies(round: LotteryRound | null): string[] {
  const combined = [
    round?.huayTypeName,
    round?.huayTypeCode,
    round?.huayName,
    round?.huayCode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (/รัฐบาลไทย|governmentthai|setgovernmentthai/.test(combined)) {
    return ['THAI_GOV_SET']
  }
  if (/ฮานอย|hanoi/.test(combined)) {
    return ['HANOI_SET']
  }
  if (/ลาว|lao/.test(combined)) {
    return ['LAO_SET']
  }

  return []
}

function resolvePreferredSetPackages(incomingPackages: BettingPackage[], round: LotteryRound | null): BettingPackage[] {
  const setPackages = incomingPackages.filter((pkg) => pkg.packageType === 'SET_BASED' && pkg.setConfig)

  if (setPackages.length <= 3) {
    return [...setPackages].sort((a, b) => (b.setConfig?.fixedPrice || 0) - (a.setConfig?.fixedPrice || 0))
  }

  const groupedFamilies = new Map<string, BettingPackage[]>()
  setPackages.forEach((pkg) => {
    const familyCode = getSetPackageFamilyCode(pkg.code)
    if (!familyCode) return
    const existing = groupedFamilies.get(familyCode) || []
    existing.push(pkg)
    groupedFamilies.set(familyCode, existing)
  })

  if (groupedFamilies.size <= 1) {
    return [...setPackages].sort((a, b) => (b.setConfig?.fixedPrice || 0) - (a.setConfig?.fixedPrice || 0))
  }

  const preferredFamilies = getPreferredSetFamilies(round)
  const rankedFamilies = [...groupedFamilies.entries()].map(([familyCode, packages]) => ({
    familyCode,
    packages,
    preferredIndex: preferredFamilies.indexOf(familyCode),
    totalPayout: packages.reduce((sum, pkg) => sum + Number(pkg.setConfig?.fixedPayout || 0), 0),
    totalDiscount: packages.reduce((sum, pkg) => sum + Number(pkg.setConfig?.discount || 0), 0),
  }))

  rankedFamilies.sort((left, right) => {
    const leftPreferred = left.preferredIndex === -1 ? Number.MAX_SAFE_INTEGER : left.preferredIndex
    const rightPreferred = right.preferredIndex === -1 ? Number.MAX_SAFE_INTEGER : right.preferredIndex

    if (leftPreferred !== rightPreferred) return leftPreferred - rightPreferred
    if (left.packages.length !== right.packages.length) return right.packages.length - left.packages.length
    if (left.totalPayout !== right.totalPayout) return right.totalPayout - left.totalPayout
    if (left.totalDiscount !== right.totalDiscount) return left.totalDiscount - right.totalDiscount
    return left.familyCode.localeCompare(right.familyCode)
  })

  const selectedFamily = rankedFamilies[0]
  const familyPackages = selectedFamily?.packages || setPackages
  return [...familyPackages].sort((a, b) => (b.setConfig?.fixedPrice || 0) - (a.setConfig?.fixedPrice || 0))
}

function resolvePackageDisplayCard(pkg: BettingPackage) {
  const override = pkg.metadata?.displayCard
  const hasCustomDisplay =
    Boolean(String(override?.title || '').trim())
    || typeof override?.payoutAmount === 'number'
    || typeof override?.discountPercent === 'number'

  if (hasCustomDisplay) {
    return {
      mode: 'custom' as const,
      title: String(override?.title || '').trim() || pkg.name || '',
      payoutAmount: Number(override?.payoutAmount ?? 0),
      discountPercent: Number(override?.discountPercent ?? 0),
    }
  }

  if (pkg.packageType === 'SET_BASED' && pkg.setConfig) {
    return {
      mode: 'set' as const,
      fixedPrice: Number(pkg.setConfig.fixedPrice ?? 0),
      fixedPayout: pkg.setConfig.fixedPayout != null ? Number(pkg.setConfig.fixedPayout) : null,
      discountPercent: Number(pkg.setConfig.discount ?? 0),
    }
  }

  if (pkg.prizeConfigs && pkg.prizeConfigs.length > 0) {
    const firstPrize = pkg.prizeConfigs[0]
    return {
      mode: 'rate' as const,
      title: firstPrize.prizeTypeName || firstPrize.prizeTypeCode || pkg.name,
      payoutAmount: Number(firstPrize.payoutRate ?? 0),
      discountPercent: Number(firstPrize.discount ?? 0),
    }
  }

  return {
    mode: 'empty' as const,
  }
}

function buildPackageIdentityHint(pkg: BettingPackage, duplicateNameCount: number): string {
  const description = String(pkg.description || '').trim()
  const code = String(pkg.code || '').trim()
  const displayCard = resolvePackageDisplayCard(pkg)
  const displayTitle =
    displayCard.mode === 'rate' || displayCard.mode === 'custom'
      ? String(displayCard.title || '').trim()
      : ''

  if (duplicateNameCount > 1) {
    if (description) return description
    if (displayTitle && displayTitle !== pkg.name) return displayTitle
    if (code) return code
  }

  if (description && (code.includes('-') || description.length <= 40)) {
    return description
  }

  return ''
}

/** หน้ากติกา/เลือกแพ็คเกจ — API และ logic เดียวกับ user-classic, UI แบบ HTD อ่านง่าย */
const ModernLobbyHuayLotteryTermsPage: React.FC = () => {
  const { themeConfig, assetConfig } = useOutletContext<LobbyHuayOutletContext>()
  const location = useLocation()
  const navigate = useNavigate()
  const locationState = location.state as {
    lotteryType?: 'yeekee' | 'other'
    round?: string | LotteryRound
    time?: string
    close?: string
    title?: string
    logo?: string
    /** เลขวินจากหน้าจับวิน — ส่งต่อไปหน้า bet */
    fromWinNumber?: { type: string; number: string }[]
  } | null

  const roundFromApi: LotteryRound | null =
    locationState?.round && typeof locationState.round === 'object' && '_id' in locationState.round
      ? (locationState.round as LotteryRound)
      : null
  const isYeekee = locationState?.lotteryType === 'yeekee'
  const isSetRound = isSetLottery(roundFromApi)

  const [packages, setPackages] = useState<BettingPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [packageLoadError, setPackageLoadError] = useState('')
  const [rulesModalOpen, setRulesModalOpen] = useState(false)
  const [cancelWindowMinutes, setCancelWindowMinutes] = useState(CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES)
  const loadPackagesRef = useRef<() => Promise<void>>()

  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])

  const getAssetUrl = useCallback(
    (path: string) => {
      const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
      return base + (path.startsWith('/') ? path : `/${path}`)
    },
    [assetConfig.CDN_BASE_URL, themeFolder]
  )

  const getRoundImageSrc = useCallback(
    (round: LotteryRound | null) => {
      if (!round) return getAssetUrl('/img/flag/th.png')
      const u = getRoundImage(round)
      if (/^https?:\/\//i.test(u)) return u
      return getAssetUrl(u)
    },
    [getAssetUrl]
  )

  const handleImgError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget
    if (el.dataset.imgFallback === '1') return
    el.dataset.imgFallback = '1'
    el.src = IMG_PLACEHOLDER
  }, [])

  const resolvePackagesForRound = useCallback((incomingPackages: BettingPackage[]) => {
    if (!isSetRound) return incomingPackages

    return resolvePreferredSetPackages(incomingPackages, roundFromApi)
  }, [isSetRound, roundFromApi])

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true)
      setPackageLoadError('')
      const tenantId = getTenantId()
      const baseUrl = (import.meta.env as { VITE_API_BASE_URL?: string }).VITE_API_BASE_URL || 'http://localhost:8081'
      const accessToken = authService.getAccessToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId,
      }
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

      if (!roundFromApi?.huayId) {
        setPackages([])
        setPackageLoadError('ไม่พบข้อมูลรอบหวย กรุณากลับไปเลือกรอบใหม่')
        return
      }

      if (!accessToken) {
        await handleAuthExpired()
        return
      }

      const response = await axios.get(
        `${baseUrl}/api/v1/lottery/tenant/${tenantId}/huays/${roundFromApi.huayId}/packages`,
        { headers, withCredentials: true }
      )
      if (response.data.success && response.data.data) {
        const packagesData: BettingPackage[] = response.data.data.packages || []
        const resolvedPackages = resolvePackagesForRound(packagesData)

        resolvedPackages.sort((a, b) => {
          const priceA = a.setConfig?.fixedPrice ?? a.prizeConfigs?.[0]?.payoutRate ?? 0
          const priceB = b.setConfig?.fixedPrice ?? b.prizeConfigs?.[0]?.payoutRate ?? 0
          return priceB - priceA
        })

        setPackages(resolvedPackages)
        return
      }

      setPackages([])
    } catch (error) {
      console.error('Error loading packages:', error)
      setPackages([])
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      const data = axios.isAxiosError(error) ? error.response?.data : undefined
      if (status != null && (await handleUnauthorizedResponse(status, data))) {
        return
      }
      setPackageLoadError('โหลดแพ็คเกจการแทงไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setLoading(false)
    }
  }, [resolvePackagesForRound, roundFromApi?.huayId])

  useEffect(() => {
    loadPackagesRef.current = loadPackages
  }, [loadPackages])

  useEffect(() => {
    let isMounted = true
    void lotteryService.getCustomerSlipCancelWindowMinutes().then((minutes) => {
      if (isMounted) setCancelWindowMinutes(minutes)
    })
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    loadPackages()
  }, [loadPackages])

  useEffect(() => {
    const handlePackageUpdate = (event: Event) => {
      const data = (event as CustomEvent)?.detail
      if (
        data?.contentType === 'betting_packages' ||
        data?.contentType === 'betting-packages' ||
        data?.type === 'betting_package_updated' ||
        data?.type === 'package_updated' ||
        data?.content?.type?.includes?.('betting_package')
      ) {
        setTimeout(() => loadPackagesRef.current?.(), 500)
      }
    }
    window.addEventListener('content_update', handlePackageUpdate as EventListener)
    window.addEventListener('betting_package_updated', handlePackageUpdate as EventListener)
    window.addEventListener('package_updated', handlePackageUpdate as EventListener)
    return () => {
      window.removeEventListener('content_update', handlePackageUpdate as EventListener)
      window.removeEventListener('betting_package_updated', handlePackageUpdate as EventListener)
      window.removeEventListener('package_updated', handlePackageUpdate as EventListener)
    }
  }, [])

  const handlePackageSelect = useCallback(
    (pkg: BettingPackage) => {
      const title = roundFromApi ? roundFromApi.huayName : isYeekee ? 'หวยจับยี่กี' : (locationState?.title ?? 'หวย')
      const logo = roundFromApi ? getRoundImage(roundFromApi) : (isYeekee ? '/img/yk.jpg' : locationState?.logo ?? '/img/flag/th.png')
      const time = roundFromApi ? lotteryService.formatTimeRemaining(roundFromApi.betEndTime) : (locationState?.time ?? '24 ชั่วโมง')
      const close = roundFromApi ? formatCloseTime(roundFromApi.betEndTime) : (locationState?.close ?? '')
      const roundLabel = roundFromApi ? (roundFromApi.roundNumber || '') : (typeof locationState?.round === 'string' ? locationState.round : '') ?? ''

      const fromWinNumber = locationState?.fromWinNumber
      if (isYeekee) {
        navigate('/lobby-huay/yeekee/bet', {
          state: {
            backTo: '/lobby-huay/lottery-terms',
            backLabel: 'กลับเลือก terms',
            title: 'หวยจับยี่กี',
            logo: '/img/yk.jpg',
            round: roundLabel,
            time,
            close,
            package: pkg,
            ...(roundFromApi && { roundFromApi }),
            ...(fromWinNumber && fromWinNumber.length > 0 && { fromWinNumber }),
          },
        })
      } else if (isSetRound && pkg.packageType === 'SET_BASED') {
        const fixedPrice = resolveSetPackageFixedPrice(pkg, { package: pkg, packageCode: pkg.code })
        navigate('/lobby-huay/set-lotto-game', {
          state: {
            backTo: '/lobby-huay/lottery-terms',
            backLabel: 'กลับเลือกแพ็กเกจ',
            title,
            logo,
            round: roundLabel,
            time,
            close,
            package: pkg,
            packageCode: pkg.code,
            fixedPrice: fixedPrice > 0 ? fixedPrice : undefined,
            roundFromApi: roundFromApi ?? undefined,
          },
        })
      } else {
        navigate('/lobby-huay/bet', {
          state: {
            backTo: '/lobby-huay/lottery-terms',
            backLabel: 'กลับเลือก terms',
            title,
            logo,
            round: roundLabel,
            time,
            close,
            package: pkg,
            roundFromApi: roundFromApi ?? undefined,
            ...(fromWinNumber && fromWinNumber.length > 0 && { fromWinNumber }),
          },
        })
      }
    },
    [navigate, roundFromApi, isSetRound, isYeekee, locationState]
  )

  const displayTitle = roundFromApi ? roundFromApi.huayName : isYeekee ? 'หวยจับยี่กี' : (locationState?.title ?? 'หวยรัฐบาลไทย')
  const displayLogo = roundFromApi ? getRoundImageSrc(roundFromApi) : getAssetUrl(isYeekee ? '/img/yk.jpg' : '/img/flag/th.png')
  const displayTime = useLotteryCountdown(roundFromApi?.betEndTime, locationState?.time ?? '24 ชั่วโมง')
  const displayClose = roundFromApi ? formatCloseTime(roundFromApi.betEndTime) : (locationState?.close ?? '')
  const packageNameCounts = useMemo(() => {
    const counts = new Map<string, number>()
    packages.forEach((pkg) => {
      const key = String(pkg.name || '').trim().toLowerCase()
      if (!key) return
      counts.set(key, (counts.get(key) || 0) + 1)
    })
    return counts
  }, [packages])

  const renderPackageBody = (pkg: BettingPackage) => {
    const displayCard = resolvePackageDisplayCard(pkg)

    if (displayCard.mode === 'custom') {
      return (
        <>
          <p className="htd-terms__label">{displayCard.title}</p>
          <p className="htd-terms__payout">
            จ่าย <span className="htd-terms__payout-num">{displayCard.payoutAmount.toLocaleString()}</span> บาท
          </p>
          <span className="htd-terms__discount">ลด {displayCard.discountPercent}%</span>
        </>
      )
    }

    if (displayCard.mode === 'set') {
      return (
        <>
          <p className="htd-terms__label">ราคา {displayCard.fixedPrice.toLocaleString()} บาท</p>
          {displayCard.fixedPayout != null && (
            <p className="htd-terms__payout">
              จ่าย <span className="htd-terms__payout-num">{displayCard.fixedPayout.toLocaleString()}</span> บาท
            </p>
          )}
          <span className="htd-terms__discount">ลด {displayCard.discountPercent}%</span>
        </>
      )
    }

    if (displayCard.mode === 'rate') {
      return (
        <>
          <p className="htd-terms__label">{displayCard.title}</p>
          <p className="htd-terms__payout">
            จ่าย <span className="htd-terms__payout-num">{displayCard.payoutAmount.toLocaleString()}</span> บาท
          </p>
          <span className="htd-terms__discount">ลด {displayCard.discountPercent}%</span>
        </>
      )
    }

    return <p className="htd-terms__empty-prize">ไม่มีข้อมูลรางวัล</p>
  }

  return (
    <>
      <div className="htd-terms">
        <div className="htd-terms__top">
          <Link
            to={isYeekee ? '/lobby-huay/yeekee' : '/lobby-huay/listall'}
            className="htd-terms__back"
          >
            <i className="fas fa-chevron-left" aria-hidden />
            {isYeekee ? 'กลับเลือกรอบ' : 'กลับหน้าหลัก'}
          </Link>
          <button
            type="button"
            className="htd-terms__rules"
            onClick={() => setRulesModalOpen(true)}
          >
            <i className="fas fa-book-open" aria-hidden />
            กติกา & วิธีดูผล
          </button>
        </div>

        <div className="htd-terms__round">
          <div className="htd-terms__round-main">
            <div className="htd-terms__logo">
              <img src={displayLogo} alt="" onError={handleImgError} />
            </div>
            <div className="htd-terms__meta">
              <h1 className="htd-terms__title" title={displayTitle}>
                {displayTitle}
              </h1>
              {displayClose ? (
                <p className="htd-terms__close">ปิดรับ {displayClose}</p>
              ) : null}
            </div>
            <div className="htd-terms__countdown" aria-live="polite">
              {displayTime}
            </div>
          </div>
        </div>

        <p className="htd-terms__hint">เลือกแพ็คเกจเพื่อเข้าหน้าแทง</p>

        {loading ? (
          <div className="htd-terms__state">
            <i className="fas fa-spinner fa-spin" aria-hidden />
            <p className="mt-2 mb-0">กำลังโหลดแพ็คเกจ...</p>
          </div>
        ) : packages.length === 0 ? (
          <div className="htd-terms__state">
            <p className="mb-0">{packageLoadError || 'ไม่พบแพ็คเกจการแทง'}</p>
          </div>
        ) : (
          <div className="htd-terms__grid" role="list">
            {packages.map((pkg, index) => {
              const duplicateNameCount =
                packageNameCounts.get(String(pkg.name || '').trim().toLowerCase()) || 0
              const packageIdentityHint = buildPackageIdentityHint(pkg, duplicateNameCount)

              return (
                <button
                  key={pkg.code}
                  type="button"
                  role="listitem"
                  className="htd-terms__card"
                  style={{ animationDelay: `${Math.min(index, 12) * 0.04}s` }}
                  onClick={() => handlePackageSelect(pkg)}
                  aria-label={`เลือกแพ็คเกจ ${pkg.name}`}
                >
                  <div className="htd-terms__card-head">
                    <h2 className="htd-terms__pkg-name" title={pkg.name}>
                      {pkg.name}
                    </h2>
                    {packageIdentityHint ? (
                      <span className="htd-terms__pkg-hint" title={packageIdentityHint}>
                        {packageIdentityHint}
                      </span>
                    ) : null}
                  </div>
                  <div className="htd-terms__card-body">{renderPackageBody(pkg)}</div>
                  <div className="htd-terms__card-cta">เลือกแพ็คเกจ →</div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      <RulesGuideModal
        isOpen={rulesModalOpen}
        onClose={() => setRulesModalOpen(false)}
        scope={roundFromApi ? {
          huayId: roundFromApi.huayId,
          huayName: roundFromApi.huayName,
          huayTypeId: roundFromApi.huayTypeId,
          huayTypeName: roundFromApi.huayTypeName,
        } : undefined}
        fallbackRules={(
          <div>
            <p>- ระบบเปิด-ปิดแทงตามเวลาที่ได้แจ้งไว้ และบริษัทสามารถพิจารณาปิดแทงก่อนกำหนดได้</p>
            <p>- ทางบริษัทสามารถคืนเลขได้ทุกกรณี แต่จะคืนก่อนที่จะออกผลรางวัล</p>
            <p>- การคืนเลขหรือคืนโพยของสมาชิก ต้องเป็นไปเงื่อนไขและเวลาที่ทางเว็บกำหนดเท่านั้น (<span className="c_primary"><strong>คืนโพยได้ภายใน {formatCustomerSlipCancelWindowText(cancelWindowMinutes)}หลังจากที่แทง</strong></span>)</p>
            <p>- หากหวยนั้นๆ มีการออกผลรางวัลก่อนที่จะปิดแทง บริษัทจะทำการยกเลิกรายการที่แทงเฉพาะบางเลขที่ได้ทำการแทงหลังจากหวยนั้นๆ ออกผล</p>
            <p>- ราคาจ่ายและส่วนลดจะลดลงตามเลขดังหรือเลขอั้น ให้สมาชิกตรวจสอบโพยและราคาจ่าย/ส่วนลดหลังการแทงทุกครั้ง</p>
            <p>- การสมัครสมาชิกหรือแนะนำชื่อตัวเองเพื่อปั่นค่าคอมมิชชั้น จะถูกพิจารณาปิดบัญชีและยึดเครดิตของบัญชีที่เกี่ยข้องทั้งหมด</p>
            <p>- สมาชิกที่จงใจเจตนาทุจริตในการเล่น จะถูกปิดบัญชีและไม่คืนเครดิตให้ทุกกรณี</p>
            <p>- กรณีที่เซฟเวอร์หรืออินเตอร์เน็ตล่มในช่วงการเปิดรับแทง และเกิดข้อโต้แย้งเกี่ยวกับการออกผลรางวัล ทางบริษัทมีสิทธิ์จะพิจารณายกเลิกรายการงวดและคืนเครดิตให้ลูกค้า</p>
            <p>- ผลรางวัลคลุมเครือหรือมีแนวโน้มที่ไม่โปร่งใส ทางบริษัทจะพิจารณายกเลิกรายการงวดและคืนเครดิตให้ลูกค้า</p>
            <p>- ในกรณีเกิดข้อพิพาทและโต้แย้งในการออกผลรางวัล การตัดสินของบริษัทถือเป็นข้อยุติและสิ้นสุด</p>
            <p>- สมาชิกที่สมัครมากกว่า 1 ยูสเซอร์ โดยใช้ชื่อเดียวกัน เพื่อผลประโยชน์ในการแทงได้มากกว่าหรือปั่นค่าแนะนำ จะถูกระงับและไม่คืนเครดิตให้ทุกรณี</p>
            <p>- สมาชิกสามารถถอนเงินออกได้เมื่อมียอดเล่น 50% และผลรางวัลออกครบแล้วเท่านั้น</p>
          </div>
        )}
        fallbackGuides={(
          <div>
            <p><u><strong>วิธีการดูผลรางวัล</strong></u></p>
            <p style={{ textAlign: 'center' }}>ใช้รางวัลของหวย 4 หลัก เช่น <strong>0572</strong></p>
            <p><u><strong>การตรวจผลรางวัล</strong></u></p>
            <table className="table table-bordered">
              <tbody>
                <tr><td className="text-nowrap" style={{ width: 70 }}>4 ตัวบน</td><td>0572</td></tr>
                <tr><td className="text-nowrap">3 ตัวตรง</td><td>572</td></tr>
                <tr><td className="text-nowrap">4 ตัวโต๊ด</td><td>0572 สลับตำแน่งทั้ง 4 หลัก</td></tr>
                <tr><td className="text-nowrap">3 ตัวโต๊ด</td><td>572 527 752 725 257 275</td></tr>
                <tr><td className="text-nowrap">2 ตัวหน้า</td><td>05</td></tr>
                <tr><td className="text-nowrap">2 ตัวหลัง</td><td>72</td></tr>
              </tbody>
            </table>
            <p>**จำกัดสิทธิ์ 1 ชุดรับรางวัลใหญ่ที่สุด รางวัลเดียว**</p>
          </div>
        )}
      />
    </>
  )
}

export default ModernLobbyHuayLotteryTermsPage
